import { useLocale } from '@repo/i18n';
import { Grid, Paper, Stack, useTheme } from '@mui/material';
import { CartItem, OctoOrder } from '@repo/types';
import Summary, { SummaryFeatures } from 'src/components/domain/summary/Summary';
import {
    ContinueRebookingButton,
    GoPayButton,
    PayWithInvoiceButton,
    PrintButton,
} from 'src/components/domain/summary/SummaryButtons';
import { findUrlQueryParam } from '@repo/common-utils/query-params-helper';
import { CheckoutTabType } from './cart/views/getTabIndex';
import { useConfigurations, useMembershipMode } from '@repo/widget-utils/widgetsConfiguration';
import { useCallback, useMemo, useState } from 'react';
import { usePriceSummary } from 'src/hooks/domain/usePriceSummary';
import useSummaryCartItemsByType from 'src/hooks/domain/useSummaryCartItemsByType';
import { SummaryCartItem } from 'src/components/domain/summary/summary-mapping';
import { getCartItemId } from '@repo/widget-utils/cart/cartUtils';
import { voucherAtom } from 'src/state/voucher/voucherAtom';
import { cartAtom } from 'src/state/cart/cartAtom';
import { nativeMemberCoverageAtom } from 'src/state/native-membership/nativeMemberCoverageAtom';
import { clearCartEvent, removeFromCartEvent } from 'src/state/cart/cart.reducer';
import EditSummaryItemModal from 'src/components/domain/summary/EditSummaryItemModal';
import { useAtomReducer } from 'src/hooks';
import { octoCartAtom } from 'src/state/octo-cart/octoCartAtom';
import useActiveOctoBookings from 'src/hooks/domain/useActiveOctoBookings';
import { cancelOctoBooking, cancelOctoOrder } from '@repo/widget-utils/services/fetchers/octo-api';
import { clearOctoCartEvent, setOctoCartDirtyEvent } from 'src/state/octo-cart/octoCart.reducer';
import useOctoOrderInCart from 'src/hooks/domain/cart/useOctoOrderInCart';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import { useAtom } from 'ximple';
import {
    calculateAppliedGiftcards,
    getPriceSummaryWithNativeVat,
} from '@repo/widget-utils/price-helper';
import { sumBy } from 'lodash-es';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { buildActivityReceiptData } from 'src/utils/print/build-receipt-data';
import { printWidgetReceipt } from 'src/utils/print/print-receipt';

export function useSummaryFeatures(
    showShare: boolean,
    enableCartClearing: boolean,
    enableItemEditing: boolean,
    activeTab: CheckoutTabType | null,
    isWaitingListMode: boolean,
): SummaryFeatures {
    const configurations = useConfigurations();

    return useMemo(() => {
        const isPaymentPage = activeTab === 'payment';

        const showPromoCodeSection = !!configurations.enablePromoCodes && !isWaitingListMode;

        const promoCodeWritable =
            !!configurations.enablePromoCodes && !(activeTab === null || isPaymentPage);

        const isGiftcardEnabled =
            !!configurations.enableGiftcards && activeTab !== null && !isPaymentPage;
        const giftcardWriteable = isGiftcardEnabled && !isWaitingListMode;

        const showGiftcardValue =
            !!configurations.enableGiftcards && isPaymentPage && !isWaitingListMode;

        return {
            showShare: showShare,
            showPromoCodeSection: showPromoCodeSection,
            promoCodeSectionSx: undefined,
            promoCodeWritable: promoCodeWritable,
            giftcardWriteable: giftcardWriteable,
            enableCartClearing: enableCartClearing,
            enableItemEditing: enableItemEditing,
            showGiftcardValue: showGiftcardValue,
        } as SummaryFeatures;
    }, [
        showShare,
        enableCartClearing,
        enableItemEditing,
        configurations,
        activeTab,
        isWaitingListMode,
    ]);
}

export default function CheckoutSummary(props: {
    goPayClicked?: () => void;
    goPayClickedInvoice?: () => void;
    isMakingReservation?: boolean;
    nextButtonText?: string;
    cartItems: CartItem[];
    hideGoPay: boolean;
    showPrintButton?: boolean;
    headingText?: string;
    summaryFeatures: SummaryFeatures;
    completedOctoOrder?: OctoOrder;
    bookingRef?: string | null;
    beforeGoPay?: React.ReactNode;
}) {
    const [{ appliedPromoCode, giftcards }] = useAtomReducer(voucherAtom);

    const { t, locale } = useLocale();
    const theme = useTheme();
    const configurations = useConfigurations();
    const [currency] = useAtom(currencyAtom);
    const customizations = useCustomizations();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editCartItem, setEditCartItem] = useState<CartItem | null>(null);

    const rebookReturnUrl = findUrlQueryParam('rebookReturnUrl');

    const { octoOrder: octoOrderInCart, isLoading: isLoadingOctoOrderInCart } =
        useOctoOrderInCart();
    const octoOrder = props.completedOctoOrder ?? octoOrderInCart;

    const activeOctoBookings = useActiveOctoBookings(octoOrder);

    // Native membership: block pay while the member-card coverage preview is recalculating or
    // failed, so the member never pays against a stale total or an un-applied discount. Inert
    // for guest/legacy.
    const [
        {
            isCalculating: nativeIsCalculating,
            error: nativeCoverageError,
            result: nativeCoverageResult,
            committedResult: nativeCommittedResult,
        },
    ] = useAtom(nativeMemberCoverageAtom);
    const isNativeMode = useMembershipMode() === 'native';
    const isNativeCalculating = isNativeMode && nativeIsCalculating;
    const isNativeCoverageError = isNativeMode && nativeCoverageError;
    // The member-card coverage to print — live during checkout, falling back to the snapshot
    // committed at reserve on the confirmation/receipt (null for guest/legacy, mirroring Summary).
    const nativeReceiptResult = isNativeMode
        ? (nativeCoverageResult ?? nativeCommittedResult)
        : null;

    const enableGoPay = useMemo(() => {
        const propsCartHasItems = props.cartItems.length > 0;

        const octoOrderHasItems = activeOctoBookings.length > 0;
        return (
            (propsCartHasItems || octoOrderHasItems) &&
            !isNativeCalculating &&
            !isNativeCoverageError
        );
    }, [props.cartItems, activeOctoBookings, isNativeCalculating, isNativeCoverageError]);

    const summaryCartItemsByType = useSummaryCartItemsByType(
        props.cartItems,
        isLoadingOctoOrderInCart,
        octoOrder,
        appliedPromoCode,
    );

    const priceSummary = usePriceSummary(
        props.cartItems,
        isLoadingOctoOrderInCart,
        octoOrder,
        appliedPromoCode,
    );

    const removeCartItem = useCallback(
        async (summaryCartItem: SummaryCartItem) => {
            if (configurations.octo) {
                await cancelOctoBooking(summaryCartItem.uniqueKey, {
                    reason: 'User requested cancellation',
                });
                await octoCartAtom.update(setOctoCartDirtyEvent());
            } else {
                const cartItem = props.cartItems.find(
                    (ci) => getCartItemId(ci) === summaryCartItem.uniqueKey,
                )!;
                cartAtom.update(removeFromCartEvent(cartItem));
            }
        },
        [configurations.octo, props.cartItems],
    );

    const onEditClick = useCallback(
        (e: React.MouseEvent, summaryCartItem: SummaryCartItem) => {
            const cartItem = props.cartItems.find(
                (ci) => getCartItemId(ci) === summaryCartItem.uniqueKey,
            )!;

            if (e.currentTarget instanceof HTMLElement) {
                setAnchorEl(e.currentTarget);
                setEditCartItem(cartItem);
            }
        },
        [props.cartItems],
    );

    const onClearCart = props.summaryFeatures.enableCartClearing
        ? async () => {
              if (configurations.octo && octoOrder) {
                  await cancelOctoOrder(octoOrder.id, {
                      reason: 'User requested cancellation',
                  });
                  await octoCartAtom.update(clearOctoCartEvent());
              } else {
                  await cartAtom.update(clearCartEvent());
              }
          }
        : undefined;

    const onPrintReceipt = useCallback(() => {
        const promoCodeDiscountAmount = priceSummary.promoCodeDiscounts.reduce(
            (acc, cur) => acc + cur.amount,
            0,
        );
        const appliedGiftcards = calculateAppliedGiftcards(giftcards, priceSummary.totalPrice);
        const giftCardDiscountAmount = sumBy(appliedGiftcards, (x) => x.priceReduction);

        // Per-line coverage is now shown on the item rows (Style B), so the receipt no longer needs a
        // separate member-card deduction line — keep only the Base-authoritative total + VAT.
        const memberCardDiscountAmount = 0;
        const memberCardTotal = nativeReceiptResult ? nativeReceiptResult.total : null;
        // Native mode: print Base's coverage-aware VAT (Eks. MVA / per-rate MVA), not the cart's gross.
        const receiptPriceSummary = nativeReceiptResult
            ? getPriceSummaryWithNativeVat(priceSummary, nativeReceiptResult)
            : priceSummary;

        const logoUrl =
            (configurations.logo && configurations.logo.length > 0
                ? configurations.logo
                : customizations.iconUrl) || null;

        const data = buildActivityReceiptData({
            bookingRef: props.bookingRef ?? null,
            summaryCartItemsByType,
            priceSummary: receiptPriceSummary,
            appliedPromoCode: appliedPromoCode?.coupon_code ?? null,
            promoCodeDiscountAmount,
            giftCardDiscountAmount,
            memberCardDiscountAmount,
            memberCardTotal,
            logoUrl,
            locale,
            currency,
            t,
        });
        printWidgetReceipt(data);
    }, [
        priceSummary,
        giftcards,
        summaryCartItemsByType,
        appliedPromoCode,
        nativeReceiptResult,
        configurations.logo,
        customizations.iconUrl,
        props.bookingRef,
        locale,
        currency,
        t,
    ]);

    return (
        <Grid
            component={Paper}
            role="complementary"
            aria-label={t.aria_order_summary}
            mt={theme.spacing(2)}
            p={theme.spacing(3, 3, 2, 3)}
            maxWidth="400px"
            width="100%"
            sx={{
                backgroundColor: theme.palette.grey[50],
                color: theme.palette.primary.main,
                [theme.breakpoints.down('md')]: {
                    width: '100%',
                    maxWidth: '440px',
                    marginTop: theme.spacing(2),
                    marginBottom: theme.spacing(2),
                },
                '@media print': {
                    '& .no-print': {
                        display: 'none',
                    },
                },
            }}
            variant="outlined"
        >
            <Summary
                heading={props.headingText}
                features={props.summaryFeatures}
                priceSummary={priceSummary}
                summaryCartItemsByType={summaryCartItemsByType}
                onRemoveCartItem={removeCartItem}
                onEditClick={onEditClick}
                onClearCart={onClearCart}
            />

            {props.showPrintButton && (
                <Grid>
                    <PrintButton text={t.print} onPrint={onPrintReceipt}></PrintButton>
                </Grid>
            )}

            {!props.hideGoPay && (
                <>
                    {props.beforeGoPay && <Grid mt={theme.spacing(3)}>{props.beforeGoPay}</Grid>}
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        gap={2}
                        mt={theme.spacing(props.beforeGoPay ? 2 : 4)}
                    >
                        {props.goPayClickedInvoice && (
                            <PayWithInvoiceButton
                                text={t.pay_with_invoice}
                                onClick={props.goPayClickedInvoice}
                                disabled={props.isMakingReservation || !enableGoPay}
                            ></PayWithInvoiceButton>
                        )}
                        <GoPayButton
                            text={`${t.next} - ${props.nextButtonText ?? t.go_and_pay}`}
                            minWidth={props.goPayClickedInvoice ? undefined : '50%'}
                            loading={props.isMakingReservation}
                            disabled={props.isMakingReservation || !enableGoPay}
                            onClick={props.goPayClicked}
                        ></GoPayButton>
                    </Stack>
                </>
            )}

            {rebookReturnUrl && (
                <Grid display="flex" justifyContent="end">
                    <ContinueRebookingButton
                        text={t.continueWithRebooking}
                        href={rebookReturnUrl}
                    ></ContinueRebookingButton>
                </Grid>
            )}

            {Boolean(anchorEl) && editCartItem && (
                <EditSummaryItemModal
                    cartItem={editCartItem}
                    anchorEl={anchorEl}
                    onClose={() => {
                        setAnchorEl(null);
                        setEditCartItem(null);
                    }}
                ></EditSummaryItemModal>
            )}
        </Grid>
    );
}
